import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface ErrorLog {
  id: string;
  message: string;
  timestamp: number;
  stack?: string;
  type: "error" | "warning" | "info";
}

interface ErrorStore {
  errors: ErrorLog[];
  addError: (error: Omit<ErrorLog, "id" | "timestamp">) => void;
  clearErrors: () => void;
  removeError: (id: string) => void;
}

export const useErrorStore = create<ErrorStore>()(
  persist(
    (set) => ({
      errors: [],
      addError: (errorData) =>
        set((state) => ({
          errors: [
            ...state.errors,
            {
              ...errorData,
              id: crypto.randomUUID(),
              timestamp: Date.now(),
            },
          ],
        })),
      clearErrors: () => set({ errors: [] }),
      removeError: (id) =>
        set((state) => ({
          errors: state.errors.filter((error) => error.id !== id),
        })),
    }),
    {
      name: "error-storage",
      partialize: (state) => ({ errors: state.errors }),
    }
  )
);

// Global error handler
export const setupGlobalErrorHandling = () => {
  const { addError } = useErrorStore.getState();

  // Catch unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    addError({
      message: event.reason?.message || "Unhandled Promise Rejection",
      stack: event.reason?.stack,
      type: "error",
    });
  });

  // Catch global errors
  window.addEventListener("error", (event) => {
    addError({
      message: event.message,
      stack: event.error?.stack,
      type: "error",
    });
  });

  // Override console.error to capture errors
  const originalConsoleError = console.error;
  console.error = (...args) => {
    addError({
      message: args.map((arg) => (arg instanceof Error ? arg.message : String(arg))).join(" "),
      type: "error",
    });
    originalConsoleError(...args);
  };
};
